import { requisitarJson } from '../../../shared/api/http';

export interface TurmaResumo {
    id: string;
    nome?: string | null;
    ativa: boolean;
    participantes: number;
}

export interface Jogador {
    id: string;
    login: string;
    nome?: string | null;
    grupo_id: string | null;
    lider: boolean;
    situacao: 'ativo' | 'inativo' | 'bloqueado';
    tablet: string | null;
    sessao_ativa: boolean;
    /** Presença do tablet pelo último sinal do WebSocket; `sessao_ativa` só diz que houve login. */
    conexao?: 'online' | 'instavel' | 'offline';
    ultimo_acesso: string | null;
}

export interface JogadoresDaTurma {
    turma_id: string;
    /** Todos os grupos da turma, inclusive os vazios: são destino possível de troca. */
    grupos: string[];
    fases_abertas: string[];
    jogadores: Jogador[];
}

/** Campos que o painel pode alterar (spec jogadores-admin §4.1). */
export type CampoDeJogador = 'grupo_id' | 'lider' | 'situacao' | 'tablet';

export type ValorDeJogador = string | boolean | null;

export interface NovoLider {
    login: string;
    aleatorio: boolean;
}

export interface JogadorAlterado {
    jogador: Jogador;
    novo_lider: string | null;
    rebaixado: string | null;
    sessao_revogada: boolean;
    /** Verdadeiro em toda troca real de grupo: o progresso individual do alocado foi apagado. */
    progresso_zerado?: boolean;
}

export function listarTurmas(): Promise<TurmaResumo[]> {
    return requisitarJson('/api/v1/jogadores/turmas');
}

export function listarJogadores(turmaId: string): Promise<JogadoresDaTurma> {
    return requisitarJson(`/api/v1/jogadores?turma_id=${encodeURIComponent(turmaId)}`);
}

export interface PontuacaoDaFase {
    fase: string;
    /** O que o jogador soma no ranking individual nesta fase; `null` sem nada apurado para ele. */
    pontos: number | null;
    /** A regra da fase leva todo lançamento para o grupo: ela nunca soma no individual. */
    pontua_o_grupo: boolean;
}

export interface PontuacaoDoJogador {
    turma_id: string;
    jogador_id: string;
    fases: PontuacaoDaFase[];
    total: number;
}

/** "Ver pontuação" (`DP-RA-010`). */
export function lerPontuacaoDoJogador(turmaId: string, jogadorId: string): Promise<PontuacaoDoJogador> {
    return requisitarJson(
        `/api/v1/jogadores/${encodeURIComponent(jogadorId)}/pontuacao?turma_id=${encodeURIComponent(turmaId)}`,
    );
}

export interface JogadorExcluido {
    turma_id: string;
    jogador_id: string;
    sessao_revogada: boolean;
    /** Matrícula do líder sorteado quando o excluído era o líder (`DP-RA-049`). */
    novo_lider: string | null;
}

/** Exclusão lógica (`RA8`, `DP-RA-043`): o histórico fica, a matrícula fica livre. */
export function excluirJogador(pedido: { id: string; turmaId: string; csrfToken: string }): Promise<JogadorExcluido> {
    return requisitarJson(
        `/api/v1/jogadores/${encodeURIComponent(pedido.id)}?turma_id=${encodeURIComponent(pedido.turmaId)}`,
        { method: 'DELETE', csrfToken: pedido.csrfToken },
    );
}

export function alterarJogador(pedido: {
    id: string;
    turmaId: string;
    campo: CampoDeJogador;
    valor: ValorDeJogador;
    valorAnterior: ValorDeJogador;
    novoLider: NovoLider | null;
    csrfToken: string;
}): Promise<JogadorAlterado> {
    return requisitarJson(`/api/v1/jogadores/${encodeURIComponent(pedido.id)}`, {
        method: 'PATCH',
        csrfToken: pedido.csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            turma_id: pedido.turmaId,
            campo: pedido.campo,
            valor: pedido.valor,
            valor_anterior: pedido.valorAnterior,
            novo_lider: pedido.novoLider,
        }),
    });
}
